import React, { useState } from "react";
import {
    Box,
    Heading,
    Text,
    Button,
    Container,
    Flex,
    Icon,
    List,
    ListItem,
    ListIcon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    useDisclosure,
    SimpleGrid,
    HStack
} from "@chakra-ui/react";
import {
    FaBalanceScale,
    FaBuilding,
    FaFileContract,
    FaLandmark,
    FaHandshake,
    FaUserTie,
    FaMoneyBillWave,
    FaCheckCircle,
    FaArrowRight
} from "react-icons/fa";
import { MdOutlineRealEstateAgent } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";
import { useLanguage } from "../../context/LanguageContext";

const iconsMap = [
    FaBalanceScale,
    FaBuilding,
    FaFileContract,
    FaLandmark,
    FaHandshake,
    FaUserTie,
    FaBalanceScale,
    FaHandshake,
    FaMoneyBillWave,
    FaBalanceScale,
    MdOutlineRealEstateAgent,
    FaUserTie
];

const ServicesPage = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const navigate = useNavigate();
    const { t, currentLanguage } = useLanguage();

    const categories = (t("services.items") || []).map((item, idx) => ({
        ...item,
        icon: iconsMap[idx] || FaBalanceScale
    }));

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        onOpen();
    };

    return (
        <Box bg="#f8fafc" minH="100vh">
            <SEO
                title="LegalTitan - Իրավաբանական ծառայություններ Հայաստանում"
                description="Պրոֆեսիոնալ իրավաբանական և փաստաբանական ծառայություններ Հայաստանում LegalTitan-ից։"
                keywords="իրավաբանական ծառայություններ, փաստաբանական ծառայություններ, իրավաբանական խորհրդատվություն, կորպորատիվ իրավունք, պայմանագրային իրավունք"
                url="https://legaltitan.am/services"
                image="https://legaltitan.am/assets/images/legalLogo.jpg"
                fbAppId="61557661694303"
            />

            {/* Executive Hero Banner */}
            <Box
                position="relative"
                minH={{ base: "380px", md: "460px" }}
                bg="#070d1e"
                color="white"
                display="flex"
                alignItems="center"
                overflow="hidden"
            >
                {/* Background image overlay */}
                <Box
                    position="absolute"
                    inset={0}
                    bgImage="url('/assets/images/services_back.jpg')"
                    bgSize="cover"
                    bgPosition="center"
                    opacity={0.3}
                />
                <Box
                    position="absolute"
                    inset={0}
                    bg="linear-gradient(to bottom, rgba(7, 13, 30, 0.7) 0%, rgba(7, 13, 30, 0.95) 100%)"
                />

                <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={2} py={{ base: 12, md: 16 }}>
                    <Box maxW="840px" mx="auto" textAlign="center">
                        <Heading
                            as="h1"
                            fontSize={{ base: "30px", sm: "40px", md: "52px" }}
                            fontWeight="900"
                            letterSpacing="-0.03em"
                            lineHeight="1.2"
                            mb={5}
                        >
                            {t("services.pageTitle")}
                        </Heading>

                        <Text
                            fontSize={{ base: "md", md: "lg", lg: "xl" }}
                            color="gray.300"
                            maxW="700px"
                            mx="auto"
                            lineHeight="1.8"
                            mb={8}
                        >
                            {t("footer.description")}
                        </Text>

                        <HStack spacing={4} justify="center">
                            <Button
                                size="lg"
                                bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                                color="#0b132b"
                                fontWeight="800"
                                borderRadius="xl"
                                px={8}
                                boxShadow="0 10px 25px rgba(223, 186, 115, 0.35)"
                                _hover={{ transform: "translateY(-2px)", boxShadow: "0 15px 30px rgba(223, 186, 115, 0.5)" }}
                                onClick={() => document.getElementById('services-grid')?.scrollIntoView({ behavior: 'smooth' })}
                                rightIcon={<FaArrowRight />}
                            >
                                {t("services.btnViewAll")}
                            </Button>
                        </HStack>
                    </Box>
                </Container>
            </Box>

            {/* 12 Law Practice Bento Grid */}
            <Box id="services-grid" py={{ base: 14, md: 24 }} position="relative">
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, lg: 8 }}>
                        {categories.map((cat, idx) => (
                            <Box
                                key={idx}
                                bg="white"
                                p={{ base: 6, md: 8 }}
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.100"
                                boxShadow="0 10px 30px -5px rgba(11, 19, 43, 0.04)"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                                position="relative"
                                overflow="hidden"
                                _hover={{
                                    transform: "translateY(-6px)",
                                    boxShadow: "0 25px 45px -10px rgba(11, 19, 43, 0.12)",
                                    borderColor: "#dfba73",
                                }}
                            >
                                <Box>
                                    <HStack justify="space-between" align="flex-start" mb={6}>
                                        <Flex
                                            w={14}
                                            h={14}
                                            borderRadius="xl"
                                            bg="rgba(11, 19, 43, 0.05)"
                                            color="#0b132b"
                                            align="center"
                                            justify="center"
                                            transition="all 0.3s ease"
                                        >
                                            <Icon as={cat.icon} fontSize="24px" />
                                        </Flex>
                                    </HStack>

                                    <Heading
                                        as="h3"
                                        fontSize={{ base: "lg", md: "xl" }}
                                        fontWeight="800"
                                        color="#0b132b"
                                        mb={4}
                                        letterSpacing="-0.01em"
                                    >
                                        {cat.title}
                                    </Heading>

                                    <List spacing={3} mb={6}>
                                        {(cat.services || []).slice(0, 3).map((srv, sIdx) => (
                                            <ListItem
                                                key={sIdx}
                                                display="flex"
                                                alignItems="flex-start"
                                                fontSize="sm"
                                                color="gray.600"
                                                lineHeight="1.6"
                                            >
                                                <ListIcon as={FaCheckCircle} color="#dfba73" mt={1} flexShrink={0} />
                                                <Text>{srv}</Text>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>

                                <Box pt={4} borderTop="1px solid" borderColor="gray.50">
                                    {(cat.services || []).length > 3 ? (
                                        <Button
                                            variant="ghost"
                                            color="#0b132b"
                                            size="sm"
                                            fontWeight="700"
                                            p={0}
                                            _hover={{ color: "#dfba73" }}
                                            rightIcon={<FaArrowRight />}
                                            onClick={() => handleCategoryClick(cat)}
                                        >
                                            {currentLanguage === "ru" ? "Все детали" : currentLanguage === "en" ? "View all details" : "Տեսնել բոլոր մանրամասները"}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            color="#0b132b"
                                            size="sm"
                                            fontWeight="700"
                                            p={0}
                                            _hover={{ color: "#dfba73" }}
                                            rightIcon={<FaArrowRight />}
                                            onClick={() => navigate('/contact')}
                                        >
                                            {t("nav.consultation")}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Category Detail Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered motionPreset="slideInBottom">
                <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
                <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="2xl">
                    <ModalHeader
                        bg="#0b132b"
                        color="white"
                        py={6}
                        px={8}
                        borderBottom="1px solid rgba(223, 186, 115, 0.2)"
                    >
                        <HStack spacing={3}>
                            <Flex
                                w={10}
                                h={10}
                                borderRadius="lg"
                                bg="rgba(223, 186, 115, 0.2)"
                                color="#dfba73"
                                align="center"
                                justify="center"
                            >
                                <Icon as={selectedCategory?.icon || FaBalanceScale} fontSize="20px" />
                            </Flex>
                            <Box>
                                <Heading as="h3" fontSize="xl" fontWeight="800">
                                    {selectedCategory?.title}
                                </Heading>
                            </Box>
                        </HStack>
                        <ModalCloseButton color="gray.300" top={6} right={6} />
                    </ModalHeader>

                    <ModalBody p={8} bg="white">
                        <List spacing={4}>
                            {selectedCategory?.services.map((service, index) => (
                                <ListItem
                                    key={index}
                                    display="flex"
                                    alignItems="flex-start"
                                    fontSize="sm"
                                    color="gray.700"
                                    lineHeight="1.7"
                                >
                                    <ListIcon as={FaCheckCircle} color="#dfba73" mt={1} boxSize={4} flexShrink={0} />
                                    <Text>{service}</Text>
                                </ListItem>
                            ))}
                        </List>
                    </ModalBody>

                    <ModalFooter bg="#f8fafc" borderTop="1px solid" borderColor="gray.100" p={5}>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={onClose}
                        >
                            {t("modal.close")}
                        </Button>
                        <Button
                            bg="#0b132b"
                            color="#dfba73"
                            _hover={{ bg: "#162044" }}
                            onClick={() => {
                                onClose();
                                navigate('/contact');
                            }}
                        >
                            {t("nav.consultation")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default ServicesPage;
